import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import MobileLayout from "../MobileLayout";
import breeferPlayButton from "../asset/icons/breeferPlayButton.png";
import { getBreefSetting } from "../services/breefGameService";
import { useCountUp } from "../lib/useCountUp";
import { getMyResult, MyResult } from "../apis/BreefApis";

/**
 * How this breef went, before the total score across all breefs. The quiz and the game each fill their own bar,
 * one after the other, and the sum counts up at the end - so the participant can see where the points came from.
 */
const ORANGE = "#EB6608";
const FILL_MS = 900;

const Bar = ({ label, points, max, show }: { label: string; points: number; max: number; show: boolean }) => {
  const shown = useCountUp(points, show);
  const width = max > 0 ? Math.min(100, (points / max) * 100) : 0;
  return (
    <div style={{ marginBottom: "1.6rem" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "0.4rem" }}>
        <span style={{ fontFamily: "Roboto", fontSize: "1.05rem", color: "#323232" }}>{label}</span>
        <span style={{ fontFamily: "Roboto", fontSize: "1.05rem", fontWeight: 700, color: ORANGE }}>
          {shown.toLocaleString("da-DK")}
          <span style={{ color: "#8b8b8b", fontWeight: 400 }}> / {max.toLocaleString("da-DK")}</span>
        </span>
      </div>
      <div style={{ height: "14px", borderRadius: "99px", background: "rgba(0,0,0,0.08)", overflow: "hidden" }}>
        <div
          style={{
            height: "100%",
            width: show ? `${width}%` : 0,
            borderRadius: "99px",
            background: ORANGE,
            transition: `width ${FILL_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`,
          }}
        />
      </div>
    </div>
  );
};

const BreefScore = () => {
  const [t] = useTranslation("global");
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0 quiz, 1 game, 2 total
  const [max, setMax] = useState({ quiz: 2000, game: 1000 });

  const saved = (() => {
    try {
      return JSON.parse(sessionStorage.getItem("gameResult") || "{}");
    } catch {
      return {};
    }
  })();
  // The server's result for this breef is the truth (also when the link is opened again on another day);
  // the phone's own numbers are the fallback while it loads
  const [server, setServer] = useState<MyResult["thisBreef"] | null>(null);
  useEffect(() => {
    getMyResult().then((result) => result.thisBreef.submitted && setServer(result.thisBreef)).catch(() => undefined);
  }, []);
  const quizScore = server ? server.quizScore : Number(saved.quizScore ?? sessionStorage.getItem("quizScore")) || 0;
  const gamePoints = server ? server.gamePoints : Number(saved.gamePoints ?? sessionStorage.getItem("gameScore")) || 0;
  const total = server ? server.breefScore : Number(saved.breefScore) || quizScore + gamePoints;
  const shownTotal = useCountUp(total, step >= 2, 1100);

  useEffect(() => {
    // The two maximums are set in admin → Breef-indstillinger
    getBreefSetting()
      .then((response: any) => {
        const setting = response?.results?.[0];
        if (setting) setMax({ quiz: Number(setting.quizMaxPoints) || 2000, game: Number(setting.gameMaxPoints) || 1000 });
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    // One bar at a time, then the sum
    const timers = [setTimeout(() => setStep(1), 400), setTimeout(() => setStep(2), 1500)];
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <MobileLayout>
      <div style={{ height: "calc(100dvh - 2rem)", display: "flex", flexDirection: "column", justifyContent: "space-between", padding: "2rem 10% 1.5rem" }}>
        <div>
          <h1 style={{ fontFamily: "Roboto", fontWeight: 350, fontSize: "2rem", margin: "1rem 0 2rem", color: "#323232" }}>{t("breefScore.title")}</h1>
          <Bar label={t("breefScore.quiz")} points={quizScore} max={max.quiz} show={step >= 0} />
          <Bar label={t("breefScore.game")} points={gamePoints} max={max.game} show={step >= 1} />
        </div>

        <div style={{ textAlign: "center" }}>
          <p style={{ fontFamily: "Roboto", fontSize: "1.05rem", color: "#666", margin: 0 }}>{t("breefScore.total")}</p>
          <p
            style={{
              fontFamily: "Roboto",
              fontWeight: 700,
              fontSize: "3.4rem",
              lineHeight: 1.1,
              margin: "0.2rem 0 0",
              color: ORANGE,
              transform: step >= 2 ? "scale(1)" : "scale(0.9)",
              opacity: step >= 2 ? 1 : 0,
              transition: "opacity 400ms ease, transform 400ms cubic-bezier(0.22, 1, 0.36, 1)",
            }}
          >
            {shownTotal.toLocaleString("da-DK")}
          </p>
        </div>

        <div style={{ textAlign: "center" }}>
          <div onClick={() => navigate("/game-result")} style={{ cursor: "pointer", display: "inline-block" }}>
            <img src={breeferPlayButton} alt={t("breefScore.next")} style={{ height: "56px", border: "none" }} />
            <p style={{ fontFamily: "Roboto", fontWeight: 700, color: ORANGE, fontSize: "1.1rem", margin: "0.3rem 0 0" }}>{t("breefScore.next")}</p>
          </div>
        </div>
      </div>
    </MobileLayout>
  );
};

export default BreefScore;
